import { useState } from "react";
import { Button } from "@component/button";
import {
    Card,
    CardContent,
} from "@component/card";
import { useNavigate } from "react-router-dom";
import { Input } from "@component/input";
import BasicLayout from "@container/layout/basic";
import { createStartupSubmission } from "@service/interview";
import { setCookie } from "@util/cookie";
import { formatHumanReadable } from "@util/date";
import { Label } from "@component/label";

export default function FirstMoveInterviewDemo() {
    const navigate = useNavigate();
    const [isStarting, setIsStarting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showOverwriteModal, setShowOverwriteModal] = useState(false);
    const [existingSubmissionData, setExistingSubmissionData] = useState<any>(null);
    const [formData, setFormData] = useState({
        email: "",
        startupName: "",
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Check if all form fields are filled
    const areAllFieldsFilled = () => {
        return Object.values(formData).every(value => value.trim() !== "");
    };

    const handleStartInterview = async (e: React.FormEvent<HTMLFormElement>, forceOverwrite = false) => {
        e.preventDefault();
        // Only proceed if all fields are filled
        if (!areAllFieldsFilled()) return;

        setIsStarting(true);
        setError(null);
        // Store form data before navigating
        localStorage.setItem('firstMoveFormData', JSON.stringify(formData));

        try {
            // Submit startup information to the API
            const response = await createStartupSubmission({
                email: formData.email,
                startup_name: formData.startupName,
                hiring_investor_id: import.meta.env.VITE_FIRST_MOVE_USER_ID,
            });

            if (response.code === 201) {
                console.log("Startup submission created:", response);
                // If you need to store the submission ID for later use
                if (response.data) {
                    localStorage.setItem('startupSubmissionId', response.data.id);
                }
                // log the user in
                setCookie("authToken", response.data?.token?.access_token ?? "", import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY);

                // Navigate to the actual interview page
                navigate("/firstmove-vibecheck/interview");
            } else if (response.code === 400) {
                // User has already attempted an interview
                setExistingSubmissionData(response.data);
                setShowOverwriteModal(true);
                setIsStarting(false);
            } else {
                setError(response.message);
                setIsStarting(false);
            }
        } catch (error) {
            console.error("Failed to submit startup information:", error);
            setError("Failed to submit your startup information. Please try again later.");
            setIsStarting(false);
        }
    };

    const handleOverwriteConfirm = async () => {
        setShowOverwriteModal(false);
        localStorage.setItem('firstMoveFormData', JSON.stringify(formData));

        try {
            // Submit startup information to the API
            console.log("Existing startup submission:", existingSubmissionData);
            // If you need to store the submission ID for later use
            if (existingSubmissionData) {
                localStorage.setItem('startupSubmissionId', existingSubmissionData.id);
            }
            // log the user in
            setCookie("authToken", existingSubmissionData?.token?.access_token ?? "", import.meta.env.VITE_AUTH_TOKEN_EXPIRED_DAY);

            // Navigate to the actual interview page
            navigate("/firstmove-vibecheck/interview");
        } catch (error) {
            console.error("Failed to submit startup information:", error);
            setError("Failed to submit your startup information. Please try again later.");
            setIsStarting(false);
        }
    };

    const handleOverwriteCancel = () => {
        setShowOverwriteModal(false);
        setIsStarting(false);
        setExistingSubmissionData(null);
    };

    const isFormValid = formData.startupName.trim() !== "" && formData.email.trim() !== "";

    return (
        <BasicLayout
            hideNavbarMenu
            disableMinHeight
            customLogo={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/firstmovefund_logo.jpeg`}
            cssRoot="!pb-0"
        >
            {/* Overwrite Confirmation Modal */}
            {showOverwriteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Interview Already Exists
                        </h3>
                        <div className="text-gray-600 mb-6">
                            <p className="mb-3">
                                You've already submitted an interview with this email address.
                            </p>
                            {existingSubmissionData && (
                                <div className="bg-gray-50 p-3 rounded border text-sm">
                                    <p><strong>Email:</strong> {existingSubmissionData.email || formData.email}</p>
                                    <p><strong>Startup:</strong> {existingSubmissionData.startup_name || "N/A"}</p>
                                    {existingSubmissionData.created_at && (
                                        <p><strong>Submitted:</strong> {formatHumanReadable(existingSubmissionData.updated_at, true)}</p>
                                    )}
                                </div>
                            )}
                            <p className="mt-3">
                                Would you like to overwrite your previous submission and start a new interview?
                            </p>
                        </div>
                        <div className="flex space-x-3">
                            <Button
                                onClick={handleOverwriteCancel}
                                variant="outline"
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleOverwriteConfirm}
                                className="flex-1 bg-black hover:bg-gray-800"
                            >
                                Yes, Overwrite
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <div className="text-center z-10 w-full">
                <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 w-full py-12 px-4 rounded-b-[3rem] shadow-2xl border-b border-gray-700 pt-16 pb-72 gap-8">
                    <div className="flex justify-center">
                        <img
                            src="https://assets.polymet.ai/capable-olive-363848"
                            alt="First Move AI Logo"
                            className="h-16 w-auto object-contain"
                        />
                    </div>
                    <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight whitespace-nowrap bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
                        AI Investor Interview
                    </h1>
                    {/* Subtitle moved from form component */}
                    <div className="flex items-center justify-center mt-4">
                        <span className="text-base text-gray-300 font-medium tracking-wide pt-0">
                            Quick Vibe Check. No Pitch. No Slides.
                        </span>
                    </div>
                </div>
            </div>

            <div className="absolute z-20 flex justify-center w-full px-4 transform transition-all duration-300 top-[280px] sm:top-[300px] bg-transparent">
                <div className="w-full max-w-lg">
                    {/* Main content card */}
                    <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border border-gray-200/50 hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1">
                        <CardContent
                            className="pt-3 pb-6 px-6 bg-white"
                            style={{
                                borderRadius: "1rem",
                                paddingRight: "32px",
                                paddingLeft: "32px",
                                paddingTop: "24px",
                            }}
                        >
                            {/* Description */}
                            <p
                                className="text-sm text-gray-600 text-center mb-6 leading-relaxed font-medium"
                                style={{ paddingTop: "16px", paddingBottom: "0px" }}
                            >
                                A short 5 minutes video call with our AI Investor to hear your story
                                and where your startup's at.
                            </p>

                            {/* Form */}
                            <form onSubmit={handleStartInterview} className="space-y-5">
                                <div className="space-y-2">
                                    <Label
                                        htmlFor="startupName"
                                        className="text-sm font-medium text-gray-800"
                                    >
                                        Startup Name
                                    </Label>
                                    <Input
                                        id="startupName"
                                        type="text"
                                        name="startupName"
                                        placeholder="Your startup's name"
                                        value={formData.startupName}
                                        onChange={handleInputChange}
                                        className="h-11 bg-gray-50/80 border-gray-200 text-black placeholder:text-gray-500 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 hover:bg-gray-50"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label
                                        htmlFor="email"
                                        className="text-sm font-medium text-gray-800"
                                    >
                                        Email
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        name="email"
                                        placeholder="your.email@example.com"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="h-11 bg-gray-50/80 border-gray-200 text-black placeholder:text-gray-500 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 hover:bg-gray-50"
                                        required
                                    />
                                </div>

                                {/* Info text */}
                                <div className="text-center text-sm text-gray-600 leading-relaxed">
                                    <p>We'll review your deck & this vibe check.</p>
                                    <p>
                                        If it's a fit for{" "}
                                        <span className="font-semibold text-black">First Move</span>,
                                        we'll reach out with next steps.
                                    </p>
                                </div>

                                {error && (
                                    <div className="text-red-500 mb-4 text-center">
                                        {error}
                                    </div>
                                )}

                                {/* Submit button */}
                                <Button
                                    disabled={isStarting || !areAllFieldsFilled()}
                                    className={`w-full h-12 font-medium rounded-full transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${isFormValid
                                        ? "bg-gradient-to-r from-gray-900 to-black text-white hover:from-black hover:to-gray-900 shadow-lg hover:shadow-xl"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                >
                                    {isStarting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Preparing Call...
                                        </>
                                    ) : error ? "Retry Interview" : "Start Interview"}
                                </Button>
                            </form>

                            {/* Footer */}
                            <div className="text-center mt-6" style={{ paddingBottom: "16px" }}>
                                <img
                                    src="https://assets.polymet.ai/nervous-tan-828931"
                                    alt="Powered by ourteam"
                                    className="mx-auto h-12 w-auto opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </BasicLayout>
    );
}
